import { makeStyles } from '@material-ui/styles'
import Theme from 'core/themes/model'

const useCreateVirtualMachineModalStyles = makeStyles<Theme>((theme) => ({
  fields: {
    display: 'grid',
    gap: 16,
    marginBottom: 32,
    height: 'fit-content',
  },
  info: {
    background: theme.palette.grey[50],
  },
  infoContent: {
    display: 'grid',
    gap: 8,
  },
  halfColumns: {
    display: 'grid',
    gap: 24,
    gridTemplateColumns: '1fr 1fr',
    alignItems: 'baseline',
  },
  spaceBelow: {
    marginBottom: 16,
    display: 'grid',
    gridTemplateColumns: 'max-content max-content',
    gap: 16,
  },
  card: {
    height: 'calc(100% - 32px)',
    alignItems: 'center',
    padding: '16px 24px',
  },
  cardHeader: {
    borderBottom: `1px solid ${theme.palette.grey[200]}`,
    paddingBottom: 8,
    marginBottom: 8,
    display: 'grid',
    gridTemplateColumns: 'auto max-content',
    alignItems: 'center',
  },
  gpuTag: {
    background: theme.palette.blue[100],
    border: `1px solid ${theme.palette.blue[700]}`,
    borderRadius: 4,
    padding: '2px 8px',
    color: theme.palette.blue[700],
    fontWeight: 600,
  },
  cardText: {
    fontSize: 12,
    marginRight: 8,
  },
  pairs: {
    display: 'grid',
    gap: 4,
  },
  separator: {
    height: 1,
    width: '100%',
    background: theme.palette.grey[200],
    margin: '16px 0px',
  },
  networkModeCards: {
    display: 'flex',
    gap: theme.spacing(4),
  },
  networkCardWrapper: {
    position: 'relative',
    width: '100%',
  },
  networkCard: {
    width: '100%',
    height: '100%',
  },
  networkCardBody: {
    display: 'grid',
    gridGap: theme.spacing(1),
    gridTemplateColumns: 'max-content 1fr max-content',
    padding: '16px',
    placeItems: 'center',
  },
  networkCardContent: {
    justifySelf: 'left',
  },
  networkInfoIcon: {
    color: theme.palette.grey[500],
    fontSize: '20px',
    cursor: 'help',
  },
  networkTooltipBody: {
    maxWidth: 250,
  },
  vmCount: {
    display: 'grid',
    gridTemplateColumns: 'auto 130px',
    gap: 16,
    alignItems: 'center',
  },
  vmCountDisabled: {
    cursor: 'not-allowed',
  },
  vmCountInput: {
    display: 'grid',
    gridTemplateColumns: 'auto max-content',
    alignItems: 'center',
    gap: 8,
  },
  vmCountText: {
    fontSize: 12,
  },
  disabledText: {
    color: theme.palette.grey[500],
  },
  tabbed: {
    display: 'grid',
    gap: theme.spacing(2),
    marginLeft: theme.spacing(6.75),
  },
  // Keep the Cloud-init editor at the modal's width so long lines scroll inside the editor
  // instead of widening the whole modal (grid items otherwise grow to fit their content).
  cloudInitEditor: {
    minWidth: 0,
    gridTemplateColumns: 'minmax(0, 1fr)',
    // The editor card's loading wrapper is a grid too; apply the same rule so the editor itself
    // (not the card) is what scrolls sideways.
    '& .progress-root': {
      minWidth: 0,
      gridTemplateColumns: 'minmax(0, 1fr)',
    },
    '& .progressContent, & .react-codemirror2': {
      minWidth: 0,
    },
  },
  passwordScreen: {
    display: 'grid',
    gap: 24,
    height: 'fit-content',
  },
  filters: {
    display: 'grid',
    gridTemplateColumns: 'max-content max-content',
    gap: 16,
  },
  warning: {
    background: theme.palette.yellow[100],
    display: 'grid',
    gap: theme.spacing(1),
  },
  switchTooltip: {
    padding: theme.spacing(1),
    color: theme.palette.common.white,
  },
  tooltipContainer: {
    display: 'flex',
    gap: theme.spacing(1),
  },
  toggleLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  tooltipLink: {
    color: theme.components.tooltip.text,
    textDecoration: 'underline',
    '& > span': {
      color: theme.components.tooltip.text,
    },
  },
  fullWidth: {
    maxWidth: '100%',
  },
  // PCD-8236: the Create-new-flavor / Choose-existing-flavor mode selector. A grid (not
  // `filters`'s max-content columns, which overflow once SelectableCard's own min-width kicks
  // in) keeps the two cards within the modal's width with no horizontal scroll.
  flavorModeCards: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 16,
  },
  quantityFieldsRow: {
    display: 'flex',
    gap: 24,
    '& > div': {
      flex: 1,
      paddingRight: 24,
      borderRight: `1px solid ${theme.palette.grey[200]}`,
    },
    '& > div:last-child': {
      borderRight: 'none',
      paddingRight: 0,
    },
  },
  quantityFieldGroup: {
    display: 'grid',
    gap: theme.spacing(1),
  },
  quantityWithUnit: {
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'center',
  },
  quantityStepperFixed: {
    flexShrink: 0,
  },
  unitDropdownWrapper: {
    width: 90,
    flexShrink: 0,
  },
  useFlavorButton: {
    marginTop: theme.spacing(1),
  },
}))

export default useCreateVirtualMachineModalStyles
